// Shared JSON-LD builders, consumed by BOTH the React pages (via Helmet, so
// SPA navigation keeps schema up to date) and the post-build prerender script
// (so crawlers that do not run JavaScript see identical structured data in the
// static HTML). Single source of truth: if a schema shape changes, it changes
// here and both outputs stay in lockstep.
//
// This module must stay free of asset imports so it can run under plain tsx.
// All imports below are pure data.

import { AREA_SERVED, FREE_ESTIMATE_OFFER, PRICE_RANGE, SPEAKABLE } from "../data/schema";
import { AGGREGATE_RATING } from "../data/servicePages/constants";
import { testimonials } from "../data/testimonials";
import { homeFaqs } from "../data/homeFaqs";
import { buildPermitHoaFaqs } from "../data/permitFaqs";
import { geoServices, type ServiceKey, type GeoServiceConfig, type FAQ } from "../data/geoServices";
import type { Town } from "../data/towns";
import type { ServicePageConfig } from "../data/servicePages/types";
import type { FAQItem, PersonTrustSignal } from "../data/articles/types";

export const SITE_URL = "https://lancasterparoofing.com";
const BRAND = "Graystone Roofing & Siding";
const PHONE = "+1-717-725-7170";
const LOGO_URL = `${SITE_URL}/graystone-logo.gif`;
const DEFAULT_OG = `${SITE_URL}/opengraph.jpg`;

type Testimonial = (typeof testimonials)[number];

/**
 * Serialize a JSON-LD node for embedding inside a <script> tag in raw HTML.
 * Escapes `<` so free-text answers can never smuggle a `</script>` terminator
 * into the prerendered pages.
 */
export function jsonLdScriptPayload(node: unknown): string {
  return JSON.stringify(node).replace(/</g, "\\u003c");
}

// ---------------------------------------------------------------------------
// Help Hub articles
// ---------------------------------------------------------------------------

export const DEFAULT_AUTHOR: PersonTrustSignal = {
  name: "Graystone Roofing & Siding Team",
  role: "Roofing & Siding Contractor, East Petersburg, PA",
};

export const DEFAULT_REVIEWER: PersonTrustSignal = {
  name: "Graystone Roofing & Siding",
  role: "Licensed PA Home Improvement Contractor",
};

export const DEFAULT_PUBLISHED = "2025-09-01";
export const DEFAULT_MODIFIED = "2026-06-27";

// #40 Derive HowTo steps from an article's H2 headings for procedural guides.
export const HOWTO_SLUG_PATTERN = /(process|what-to-expect|step-by-step|how-to-|checklist)/;

export function buildHowToSteps(markdown: string): { name: string; text: string }[] {
  const lines = markdown.split("\n");
  const steps: { name: string; text: string }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const heading = /^##\s+(.+)$/.exec(lines[i].trim());
    if (!heading) continue;
    const name = heading[1].replace(/[*_`#]/g, "").trim();
    let text = "";
    for (let j = i + 1; j < lines.length; j++) {
      const l = lines[j].trim();
      if (!l) continue;
      if (l.startsWith("#")) break;
      text = l.replace(/[*_`>|]/g, " ").replace(/\s+/g, " ").trim();
      break;
    }
    steps.push({ name, text: text || name });
  }
  return steps;
}

export interface ArticleJsonLdInput {
  slug: string;
  title: string;
  category: string;
  excerpt: string;
  shortAnswer: string;
  markdown: string;
  faqs: FAQItem[];
  datePublished?: string;
  dateModified?: string;
  author?: PersonTrustSignal;
  /** Absolute URL of the article's hero/OG image. Defaults to the site OG image. */
  imageUrl?: string;
}

/** BlogPosting (+speakable), FAQPage, BreadcrumbList, and conditional HowTo. */
export function buildArticleJsonLd(a: ArticleJsonLdInput): Record<string, unknown> {
  const author = a.author ?? DEFAULT_AUTHOR;
  const published = a.datePublished ?? DEFAULT_PUBLISHED;
  const modified = a.dateModified ?? DEFAULT_MODIFIED;
  const articleUrl = `${SITE_URL}/${a.slug}/`;
  const image = a.imageUrl ?? DEFAULT_OG;

  const isHowTo = HOWTO_SLUG_PATTERN.test(a.slug);
  const howToSteps = isHowTo ? buildHowToSteps(a.markdown) : [];
  const howToNode =
    isHowTo && howToSteps.length >= 3
      ? {
          "@type": "HowTo",
          name: a.title,
          description: a.shortAnswer,
          step: howToSteps.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.name,
            text: s.text,
          })),
        }
      : null;

  return {
    "@context": "https://schema.org",
    "@graph": [
      ...(howToNode ? [howToNode] : []),
      {
        "@type": "BlogPosting",
        headline: a.title,
        description: a.excerpt,
        url: articleUrl,
        mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
        speakable: SPEAKABLE,
        image,
        datePublished: published,
        dateModified: modified,
        articleSection: a.category,
        author: {
          "@type": "Organization",
          name: author.name,
          url: `${SITE_URL}${AUTHOR_PAGE_PATH}`,
        },
        publisher: {
          "@type": "Organization",
          name: BRAND,
          url: SITE_URL,
          logo: { "@type": "ImageObject", url: LOGO_URL },
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: a.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Help Hub", item: `${SITE_URL}/blog/` },
          { "@type": "ListItem", position: 3, name: a.title, item: articleUrl },
        ],
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Category hub pages + author profile page
// ---------------------------------------------------------------------------

export const AUTHOR_PAGE_PATH = "/author/graystone-roofing-siding-team/";

export interface CategoryJsonLdInput {
  slug: string;
  title: string;
  description: string;
  /** Ordered list of articles shown on the hub. */
  posts: { slug: string; title: string }[];
}

/** CollectionPage + ItemList + BreadcrumbList for a category hub page. */
export function buildCategoryJsonLd(c: CategoryJsonLdInput): Record<string, unknown> {
  const pageUrl = `${SITE_URL}/category/${c.slug}/`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": pageUrl,
        name: c.title,
        description: c.description,
        url: pageUrl,
        isPartOf: { "@type": "WebSite", "@id": `${SITE_URL}/#website` },
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: c.posts.length,
          itemListElement: c.posts.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.title,
            url: `${SITE_URL}/${p.slug}/`,
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Help Hub", item: `${SITE_URL}/blog/` },
          { "@type": "ListItem", position: 3, name: c.title, item: pageUrl },
        ],
      },
    ],
  };
}

/**
 * ProfilePage for the team byline used across all Help Hub articles. The
 * byline entity is the company team (not an invented persona), so mainEntity
 * is the Organization — matching the author node in buildArticleJsonLd.
 */
export function buildAuthorPageJsonLd(): Record<string, unknown> {
  const pageUrl = `${SITE_URL}${AUTHOR_PAGE_PATH}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfilePage",
        "@id": pageUrl,
        name: `${DEFAULT_AUTHOR.name} | Author Profile`,
        url: pageUrl,
        isPartOf: { "@type": "WebSite", "@id": `${SITE_URL}/#website` },
        mainEntity: {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          name: DEFAULT_AUTHOR.name,
          description:
            "The field crews, estimators, and office staff of Graystone Roofing & Siding, a licensed and insured Lancaster County roofing and siding contractor in business since 2007.",
          url: `${SITE_URL}/`,
          telephone: PHONE,
          address: {
            "@type": "PostalAddress",
            streetAddress: "2579 Graystone Rd",
            addressLocality: "East Petersburg",
            addressRegion: "PA",
            postalCode: "17520",
            addressCountry: "US",
          },
          knowsAbout: [
            "Roof replacement",
            "Roof repair",
            "Asphalt shingle roofing",
            "Metal roofing",
            "Rubber and flat roofing",
            "Vinyl and insulated siding",
            "Seamless gutters and gutter guards",
            "Storm damage and insurance claims",
          ],
          sameAs: [
            "https://www.facebook.com/graystoneRoofingSiding",
            "https://www.bbb.org/us/pa/east-petersburg/profile/roofing-contractors/graystone-roofing-siding",
          ],
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "About the Team", item: pageUrl },
        ],
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Homepage
// ---------------------------------------------------------------------------

/** LocalBusiness, Organization, WebSite, Service catalog, and FAQPage. */
export function buildHomeJsonLd(): Record<string, unknown>[] {
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "RoofingContractor"],
    "@id": `${SITE_URL}/#localbusiness`,
    name: BRAND,
    description:
      "Locally owned roofing and siding contractor in East Petersburg, PA, serving Lancaster County since 2007 with roof repair, roof replacement, siding, and seamless gutters.",
    image: DEFAULT_OG,
    logo: LOGO_URL,
    url: `${SITE_URL}/`,
    telephone: PHONE,
    priceRange: PRICE_RANGE,
    address: {
      "@type": "PostalAddress",
      streetAddress: "2579 Graystone Rd",
      addressLocality: "East Petersburg",
      addressRegion: "PA",
      postalCode: "17520",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 40.1256, longitude: -76.3597 },
    areaServed: [
      { "@type": "AdministrativeArea", name: "Lancaster County", addressRegion: "PA" },
      { "@type": "AdministrativeArea", name: "York County", addressRegion: "PA" },
      { "@type": "AdministrativeArea", name: "Chester County", addressRegion: "PA" },
      { "@type": "AdministrativeArea", name: "Berks County", addressRegion: "PA" },
      { "@type": "AdministrativeArea", name: "Dauphin County", addressRegion: "PA" },
      { "@type": "AdministrativeArea", name: "Cumberland County", addressRegion: "PA" },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: AGGREGATE_RATING.ratingValue,
      reviewCount: AGGREGATE_RATING.reviewCount,
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "07:00",
        closes: "18:00",
      },
    ],
    sameAs: [
      "https://www.facebook.com/graystoneRoofingSiding",
      "https://www.bbb.org/us/pa/east-petersburg/profile/roofing-contractors/graystone-roofing-siding",
    ],
  };

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: BRAND,
    url: `${SITE_URL}/`,
    logo: LOGO_URL,
    image: DEFAULT_OG,
    telephone: PHONE,
    address: {
      "@type": "PostalAddress",
      streetAddress: "2579 Graystone Rd",
      addressLocality: "East Petersburg",
      addressRegion: "PA",
      postalCode: "17520",
      addressCountry: "US",
    },
    sameAs: [
      "https://www.facebook.com/graystoneRoofingSiding",
      "https://www.bbb.org/us/pa/east-petersburg/profile/roofing-contractors/graystone-roofing-siding",
    ],
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: BRAND,
    url: `${SITE_URL}/`,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };

  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Roofing",
    provider: { "@id": `${SITE_URL}/#localbusiness` },
    areaServed: { "@type": "AdministrativeArea", name: "Lancaster County, PA" },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Roofing Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Roof Repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Roof Replacement" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Roof Inspection" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Storm Damage Repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Metal Roofing" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Flat & Rubber Roofing" } },
      ],
    },
  };

  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: homeFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return [localBusiness, organization, website, service, faqPage];
}

// ---------------------------------------------------------------------------
// Service pages
// ---------------------------------------------------------------------------

/** Real testimonials only. Prioritize matches against this service, then fill. */
export function selectServiceTestimonials(filters: string[]): Testimonial[] {
  const matches = testimonials.filter((t) =>
    filters.some((f) => t.service.toLowerCase().includes(f.toLowerCase())),
  );
  return [...matches, ...testimonials.filter((t) => !matches.includes(t))].slice(0, 3);
}

/** Business + Service + WebPage + FAQPage graph for a service page. */
export function buildServicePageJsonLd(
  config: ServicePageConfig,
  ogImage: string,
): Record<string, unknown> {
  const canonical = `${SITE_URL}${config.canonicalPath}`;
  const shownTestimonials = selectServiceTestimonials(config.testimonialServiceFilter ?? []);
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": config.schemaType,
        "@id": `${canonical}#business`,
        name: BRAND,
        telephone: PHONE,
        url: SITE_URL,
        image: ogImage,
        address: {
          "@type": "PostalAddress",
          streetAddress: "East Petersburg",
          addressLocality: "East Petersburg",
          addressRegion: "PA",
          postalCode: "17520",
          addressCountry: "US",
        },
        areaServed: AREA_SERVED,
        priceRange: PRICE_RANGE,
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: AGGREGATE_RATING.ratingValue,
          reviewCount: AGGREGATE_RATING.reviewCount,
          bestRating: "5",
        },
        review: shownTestimonials.map((t) => ({
          "@type": "Review",
          author: { "@type": "Person", name: t.name },
          reviewRating: {
            "@type": "Rating",
            ratingValue: String(t.rating ?? 5),
            bestRating: "5",
          },
          reviewBody: t.text,
        })),
      },
      {
        "@type": "Service",
        "@id": `${canonical}#service`,
        name: config.serviceType,
        serviceType: config.serviceType,
        provider: { "@id": `${canonical}#business` },
        areaServed: AREA_SERVED,
        offers: FREE_ESTIMATE_OFFER,
        url: canonical,
      },
      {
        "@type": "WebPage",
        "@id": `${canonical}#webpage`,
        url: canonical,
        name: config.seoTitle,
        speakable: SPEAKABLE,
      },
      {
        "@type": "FAQPage",
        "@id": `${canonical}#faq`,
        mainEntity: config.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// Geo (town) pages
// ---------------------------------------------------------------------------

export interface GeoFaqSets {
  faqs: FAQ[];
  permitFaqs: FAQ[];
  allFaqs: FAQ[];
}

/** The exact FAQ sets a geo page renders and mirrors into its FAQ schema. */
export function getGeoFaqs(town: Town, service: ServiceKey): GeoFaqSets {
  const config: GeoServiceConfig = geoServices[service];
  const faqs = config.faqs(town);
  const permitFaqs = buildPermitHoaFaqs(town, service);
  return { faqs, permitFaqs, allFaqs: [...faqs, ...permitFaqs] };
}

/** Real testimonials only. Prioritize this town / nearby areas, then fill. */
export function selectGeoTestimonials(town: Town): Testimonial[] {
  const cityOf = (loc: string) => loc.split(",")[0].trim();
  const localMatches = testimonials.filter(
    (r) => cityOf(r.location) === town.name || town.nearbyAreas.includes(cityOf(r.location)),
  );
  return [...localMatches, ...testimonials.filter((r) => !localMatches.includes(r))].slice(0, 3);
}

/** Business, Product, Service, WebPage, and FAQPage schemas for a geo page. */
export function buildGeoPageJsonLd(town: Town, service: ServiceKey): Record<string, unknown>[] {
  const config = geoServices[service];
  const { allFaqs } = getGeoFaqs(town, service);
  const shownTestimonials = selectGeoTestimonials(town);

  const businessSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": config.schemaType,
    name: BRAND,
    telephone: PHONE,
    url: SITE_URL,
    image: DEFAULT_OG,
    priceRange: PRICE_RANGE,
    address: {
      "@type": "PostalAddress",
      addressLocality: "East Petersburg",
      addressRegion: "PA",
      postalCode: "17520",
    },
    areaServed: [
      { "@type": "AdministrativeArea", name: `${town.name}, ${town.county}, PA` },
      ...AREA_SERVED,
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: AGGREGATE_RATING.ratingValue,
      reviewCount: AGGREGATE_RATING.reviewCount,
      bestRating: "5",
    },
    review: shownTestimonials.map((t) => ({
      "@type": "Review",
      author: { "@type": "Person", name: t.name },
      reviewRating: {
        "@type": "Rating",
        ratingValue: String(t.rating ?? 5),
        bestRating: "5",
      },
      reviewBody: t.text,
    })),
  };

  const productSchema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: "Roofing and Siding Contractor",
    brand: {
      "@type": "Organization",
      name: BRAND,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: AGGREGATE_RATING.ratingValue,
      bestRating: "5",
      worstRating: "1",
      reviewCount: AGGREGATE_RATING.reviewCount,
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${config.label} contractor`,
    provider: {
      "@type": "HomeAndConstructionBusiness",
      name: BRAND,
      telephone: PHONE,
    },
    areaServed: [
      { "@type": "AdministrativeArea", name: `${town.name}, ${town.county}, PA` },
      ...AREA_SERVED,
    ],
    offers: FREE_ESTIMATE_OFFER,
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: config.canonical(town),
    name: config.seoTitle(town),
    speakable: SPEAKABLE,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: allFaqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.links?.length
          ? `${f.answer} Official references: ${f.links
              .map((l) => `${l.label} (${l.url})`)
              .join("; ")}`
          : f.answer,
      },
    })),
  };

  return [businessSchema, productSchema, serviceSchema, webPageSchema, faqSchema];
}
